import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { 
  Loader2, AlertCircle, AlertTriangle, Phone, RefreshCw, Gift, Edit,
  Heart, User, CreditCard, Upload, Camera, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { PushNotificationToggle } from '../components/PushNotificationToggle';
import { DigitalCard } from './dashboard/DigitalCard';
import { MembershipStatus } from './dashboard/MembershipStatus';
import { ReferralSection } from './dashboard/ReferralSection';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const InfoField = ({ label, value, icon }) => (
  <div className="bg-white/5 rounded-lg p-3 border border-white/5">
    <div className="flex items-center gap-1.5 mb-1">
      <span className="text-gray-600">{icon}</span>
      <p className="text-gray-500 text-[10px] uppercase tracking-wide">{label}</p>
    </div>
    <p className="text-white text-sm font-medium truncate">{value || <span className="text-gray-600 italic">Sin registro</span>}</p>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const ineInputRef = useRef(null);
  const [membership, setMembership] = useState(null);
  const [profile, setProfile] = useState(null);
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRenew, setAutoRenew] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [ineUploading, setIneUploading] = useState(false);

  const fetchData = useCallback(async () => {
    // Fetch each resource independently so one failure doesn't block the others
    const fetchers = [
      axios.get(`${API_URL}/api/membership/status`, { withCredentials: true })
        .then(r => setMembership(r.data))
        .catch(err => { if (err.response?.status !== 401) console.error('Membership fetch error:', err); }),
      axios.get(`${API_URL}/api/user/profile`, { withCredentials: true })
        .then(r => setProfile(r.data))
        .catch(err => { if (err.response?.status !== 401) console.error('Profile fetch error:', err); }),
      axios.get(`${API_URL}/api/user/referral`, { withCredentials: true })
        .then(r => setReferral(r.data))
        .catch(err => { if (err.response?.status !== 401) console.error('Referral fetch error:', err); })
    ];
    await Promise.all(fetchers);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getDaysRemaining = () => {
    const m = membership?.membership;
    if (!m?.expiry_date) return null;
    const diff = new Date(m.expiry_date) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const handleBuySubscription = async (forGift = false) => {
    try {
      const response = await axios.post(`${API_URL}/api/payment/create-checkout`, {
        auto_renew: autoRenew, for_gift: forGift
      }, { withCredentials: true });
      if (response.data.checkout_url) window.location.href = response.data.checkout_url;
    } catch (err) { toast.error('Error al iniciar el pago'); }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/user/profile`, editData, { withCredentials: true });
      toast.success('Datos actualizados');
      setEditOpen(false);
      fetchData();
    } catch (err) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleIneUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('El archivo no debe superar 10MB'); return; }
    setIneUploading(true);
    try {
      const formData = new FormData();
      formData.append('ine_file', file);
      await axios.post(`${API_URL}/api/user/upload-ine`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('INE cargada exitosamente');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al subir la INE'); }
    finally { setIneUploading(false); }
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#0D0D0D', scale: 2, useCORS: true, logging: false
      });
      const link = document.createElement('a');
      link.download = `SOS_FindMed_${membership?.membership?.card_code || 'card'}.png`;
      link.href = canvas.toDataURL();
      link.click();
      toast.success('Tarjeta descargada');
    } catch (err) { toast.error('Error al descargar'); }
    finally { setDownloading(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D] pt-16"><Loader2 className="w-8 h-8 animate-spin text-[#C81D25]" /></div>;
  }

  const m = membership?.membership;
  const daysLeft = getDaysRemaining();
  const hasMembership = membership?.has_membership;
  const needsRenewal = m?.status === 'consumed' || m?.status === 'expired';
  const isActive = m?.status === 'active';
  const isPending = m?.status === 'pending';

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-20 pb-12 px-4" data-testid="user-dashboard">
      <div className="max-w-2xl mx-auto space-y-6">

        {hasMembership && <DigitalCard cardRef={cardRef} m={m} profile={profile} user={user} downloading={downloading} downloadCard={downloadCard} />}

        {!hasMembership && (
          <Card className="border border-[#C81D25]/30 bg-[#111] shadow-2xl" data-testid="buy-membership-card">
            <CardHeader className="bg-gradient-to-r from-[#C81D25] to-[#8B0000] text-white rounded-t-xl">
              <CardTitle className="text-xl flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Completa tu compra</CardTitle>
              <CardDescription className="text-white/80">Tu cuenta fue creada pero aun no tienes membresia activa</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="bg-white/5 rounded-xl p-4 flex items-center justify-between border border-white/10">
                <div><p className="font-bold text-white text-lg">Membresia Anual</p><p className="text-gray-400 text-sm">Ambulancia 24/7 por 1 anio</p></div>
                <p className="text-2xl font-bold text-[#FFD700]">$899 MXN</p>
              </div>
              <label className="flex items-center gap-3 cursor-pointer bg-white/5 p-3 rounded-lg border border-white/10">
                <input type="checkbox" checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 text-[#C81D25] focus:ring-[#C81D25]" data-testid="auto-renew-checkbox" />
                <div><span className="text-sm font-medium text-white">Renovacion automatica</span><p className="text-xs text-gray-500">Se renueva cada anio. Cancela cuando quieras.</p></div>
              </label>
              <Button onClick={() => handleBuySubscription(false)} className="w-full bg-[#C81D25] hover:bg-[#A6141B] text-white py-6 text-lg font-bold" data-testid="complete-purchase-btn">
                Completar Pago — $899 MXN
              </Button>
            </CardContent>
          </Card>
        )}

        {hasMembership && <MembershipStatus m={m} daysLeft={daysLeft} isActive={isActive} isPending={isPending} needsRenewal={needsRenewal} handleBuySubscription={handleBuySubscription} />}

        {/* Emergency Number */}
        <a href="tel:5525671884" className="block" data-testid="emergency-call-card">
          <Card className="border border-[#C81D25]/30 bg-gradient-to-r from-[#C81D25]/10 to-[#8B0000]/10 shadow-xl hover:border-[#C81D25]/60 transition-colors cursor-pointer">
            <CardContent className="p-6 text-center">
              <Phone className="w-10 h-10 mx-auto mb-3 text-[#C81D25]" />
              <p className="text-gray-400 text-xs tracking-widest uppercase mb-1">Linea de Emergencias 24/7</p>
              <p className="text-3xl font-black text-[#C81D25]" style={{ textShadow: '0 0 30px rgba(200,29,37,0.3)' }}>55 2567 1884</p>
              <p className="text-gray-500 text-xs mt-2">Toca para llamar</p>
            </CardContent>
          </Card>
        </a>

        {/* Personal Data */}
        <Card className="border border-white/10 bg-[#111] shadow-xl" data-testid="personal-data-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-base flex items-center gap-2"><User className="w-4 h-4 text-gray-400" />Mis Datos</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => {
                setEditData({
                  phone: profile?.phone || '', emergency_contact_name: profile?.emergency_contact_name || '',
                  emergency_contact_phone: profile?.emergency_contact_phone || '', blood_type: profile?.blood_type || '', allergies: profile?.allergies || ''
                });
                setEditOpen(true);
              }} className="text-gray-400 hover:text-white hover:bg-white/10" data-testid="edit-profile-btn">
                <Edit className="w-4 h-4 mr-1" />Editar
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <InfoField label="Nombre" value={profile?.name} icon={<User className="w-3 h-3" />} />
              <InfoField label="Telefono" value={profile?.phone} icon={<Phone className="w-3 h-3" />} />
              <InfoField label="Contacto de Emergencia" value={profile?.emergency_contact_name} icon={<Heart className="w-3 h-3" />} />
              <InfoField label="Tel. Contacto" value={profile?.emergency_contact_phone} icon={<Phone className="w-3 h-3" />} />
              <InfoField label="Tipo de Sangre" value={profile?.blood_type} icon={<Heart className="w-3 h-3" />} />
              <InfoField label="Alergias" value={profile?.allergies} icon={<AlertCircle className="w-3 h-3" />} />
            </div>
          </CardContent>
        </Card>

        {/* INE Upload */}
        {profile && !profile.ine_uploaded && (
          <Card className="border border-amber-500/30 bg-gradient-to-br from-[#1a1500]/60 to-[#111] shadow-xl" data-testid="ine-upload-card">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
                  <Camera className="w-6 h-6 text-amber-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-base mb-1">Sube tu INE</h3>
                  <p className="text-gray-400 text-sm mb-4">Para verificar tu identidad al momento del servicio.</p>
                  <input ref={ineInputRef} type="file" accept="image/*" capture="environment" onChange={handleIneUpload} className="hidden" data-testid="dashboard-ine-file-input" />
                  <Button onClick={() => ineInputRef.current?.click()} disabled={ineUploading} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="dashboard-upload-ine-btn">
                    {ineUploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Subiendo...</> : <><Upload className="w-4 h-4 mr-2" />Subir INE</>}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {profile && profile.ine_uploaded && (
          <div className="flex items-center gap-2 px-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-xs">Identidad verificada con INE</span>
          </div>
        )}

        {/* Referral Program */}
        {referral && <ReferralSection referral={referral} />}

        {/* Gift */}
        <Card className="border border-[#FFD700]/20 bg-gradient-to-br from-[#1a1500]/80 to-[#111] shadow-xl" data-testid="gift-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)' }}>
                <Gift className="w-6 h-6 text-black" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold text-base mb-1">Regala Proteccion</h3>
                <p className="text-gray-400 text-sm mb-4">Protege a un familiar o ser querido con una membresia SOS FindMed.</p>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => navigate('/register')} className="bg-[#FFD700] hover:bg-[#FFA500] text-black font-bold" data-testid="buy-gift-btn">
                    <Gift className="w-4 h-4 mr-2" />Comprar para Familiar
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/register')} className="border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10" data-testid="buy-gift-another-btn">
                    <CreditCard className="w-4 h-4 mr-2" />Regalar Membresia
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Refresh + Notifications */}
        <div className="flex items-center justify-center gap-4">
          <PushNotificationToggle />
          <Button variant="ghost" onClick={fetchData} className="text-gray-500 hover:text-gray-300" data-testid="refresh-status-button">
            <RefreshCw className="w-4 h-4 mr-2" />Actualizar
          </Button>
        </div>
      </div>

      {/* Edit Profile Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md bg-[#111] border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Mis Datos</DialogTitle>
            <DialogDescription className="text-gray-400">Actualiza tu informacion personal y de emergencia</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label className="text-gray-300">Telefono</Label><Input value={editData.phone || ''} onChange={(e) => setEditData({...editData, phone: e.target.value})} className="bg-white/10 border-white/10 text-white" data-testid="edit-phone" /></div>
            <div className="space-y-2"><Label className="text-gray-300">Contacto de Emergencia</Label><Input value={editData.emergency_contact_name || ''} onChange={(e) => setEditData({...editData, emergency_contact_name: e.target.value})} className="bg-white/10 border-white/10 text-white" data-testid="edit-emergency-name" /></div>
            <div className="space-y-2"><Label className="text-gray-300">Tel. Contacto Emergencia</Label><Input value={editData.emergency_contact_phone || ''} onChange={(e) => setEditData({...editData, emergency_contact_phone: e.target.value})} className="bg-white/10 border-white/10 text-white" data-testid="edit-emergency-phone" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-gray-300">Tipo de Sangre</Label><Input value={editData.blood_type || ''} onChange={(e) => setEditData({...editData, blood_type: e.target.value})} className="bg-white/10 border-white/10 text-white" placeholder="Ej: O+" data-testid="edit-blood-type" /></div>
              <div className="space-y-2"><Label className="text-gray-300">Alergias</Label><Input value={editData.allergies || ''} onChange={(e) => setEditData({...editData, allergies: e.target.value})} className="bg-white/10 border-white/10 text-white" placeholder="Ej: Penicilina" data-testid="edit-allergies" /></div>
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setEditOpen(false)} className="border-white/10 text-gray-300 hover:bg-white/10">Cancelar</Button>
            <Button onClick={handleSaveProfile} disabled={saving} className="bg-[#C81D25] hover:bg-[#A6141B]" data-testid="save-profile-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
